import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase/config";
import { collection, query, where, onSnapshot, orderBy, limit, updateDoc, doc } from "firebase/firestore";
import { useUnreadNotificationsCount } from '../../hooks/useUnreadNotificationsCount';
import { NOTIFICATION_TYPES } from "../../services/notificationService";

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);

  // Hook pour les notifications timeline (seulement pour admins)
  const { count: unreadCount } = useUnreadNotificationsCount();

  // Vérifier si l'utilisateur est un client
  const isClient = user?.role === "client";

  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Charger les notifications timeline (SEULEMENT pour les admins)
  useEffect(() => {
    if (!user?.uid || isClient) return;
    
    // Types timeline selon le notificationService.js
    const timelineTypes = [
      NOTIFICATION_TYPES.TIMELINE_STEP,
      NOTIFICATION_TYPES.TIMELINE_UPDATE,
      NOTIFICATION_TYPES.PROJECT_STATUS,
      NOTIFICATION_TYPES.PROJECT_CREATED,
      NOTIFICATION_TYPES.DOCUMENT_UPLOADED,
      NOTIFICATION_TYPES.SYSTEM_ALERT,
      // Garder aussi les anciens types pour compatibilité
      "nouvelle_etape", "etape_validee", "dossier_cloture", "deadline_proche"
    ];
    
    // Construire la requête pour récupérer SEULEMENT les notifications timeline
    const q = query(
      collection(db, "notifications"),
      where("userId", "==", user.uid),
      where("type", "in", timelineTypes),
      orderBy("createdAt", "desc"),
      limit(5)
    );
    
    // Écouter les mises à jour en temps réel
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setNotifications(notifs);
    });
    
    return () => unsubscribe();
  }, [user, isClient]);

  // Marquer une notification comme lue
  const markAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, "notifications", notificationId);
      await updateDoc(notificationRef, {
        lu: true
      });
    } catch (err) {
      console.error("Erreur lors du marquage de la notification:", err);
    }
  };

  // Gérer le clic sur une notification
  const handleNotificationClick = async (notification) => {
    // Marquer comme lue
    if (!notification.lu) {
      await markAsRead(notification.id);
    }
    
    // Naviguer vers la cible appropriée selon le rôle de l'utilisateur
    if (notification.discussionId && notification.dossierId) {
      if (user.role === "client") {
        navigate(`/client/discussions/${notification.discussionId}?dossier=${notification.dossierId}`);
      } else {
        navigate(`/admin/dossiers/${notification.dossierId}/discussions/${notification.discussionId}`);
      }
    } else if (notification.dossierId) {
      if (user.role === "client") {
        navigate(`/client/dossiers/${notification.dossierId}`);
      } else {
        navigate(`/admin/dossiers/${notification.dossierId}`);
      }
    }
    
    // Fermer le dropdown
    setShowNotifications(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Erreur lors de la déconnexion :", error);
    }
  };

  return (
    <nav className="flex justify-between items-center p-4 bg-gray-200 dark:bg-gray-800 shadow-md">
      <div>
        <Link
          to="/"
          className="text-2xl font-bold text-gray-800 dark:text-gray-100"
        >
          EnergeSync
        </Link>
      </div>
      
      {/* Menu conditionnel selon le rôle */}
      <div className="flex space-x-4 items-center">
        {!isClient && (
          <>
            <Link
              to="/"
              className="text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400"
            >
              Accueil
            </Link>
            <Link
              to="/dashboard"
              className="text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400"
            >
              Projets
            </Link>
            {/* Abonnements - seulement pour super admin */}
            {user?.role === "superadmin" && (
              <Link
                to="/subscription"
                className="text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400"
              >
                Abonnements
              </Link>
            )}
            <Link
              to="/contact"
              className="text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400"
            >
              Contact
            </Link>
          </>
        )}
        
        {user ? (
          <>
            {/* Centre de notifications - SUPPRIMÉ pour tous les rôles */}
            {/* Les notifications sont maintenant dans le sidebar avec séparation messagerie/timeline */}
            
            <span className="text-gray-800 dark:text-gray-100">
              {user.email}
            </span>
            <button
              onClick={handleLogout}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
            >
              Déconnexion
            </button>
          </>
        ) : (
          <>
            <Link
              to="/login"
              className="text-blue-600 hover:underline dark:text-blue-400"
            >
              Connexion
            </Link>
            <Link
              to="/register"
              className="text-blue-600 hover:underline dark:text-blue-400"
            >
              Inscription
            </Link>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navbar;